import { useState } from 'react';
import { SaveButton, TabContentWrapper } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import InvoiceNoteItemsSelectServiceTabs from './InvoiceNoteItemsSelectServiceTabs';
import { Form, message } from 'antd';
import {
  InvoiceItemFormValues,
  TypeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useCreateInvoiceNoteItems } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItms.hooks';

const initialTabErrors = {
  general: false,
  values: false,
  iss: false,
};

export default function InvoiceNoteItemServiceForm({ route }: PageProps) {
  const intl = useIntl().formatMessage;

  const [tabErrors, setTabErrors] = useState(initialTabErrors);
  const [loadingValidate, setLoadingValidate] = useState(false);

  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const [form] = Form.useForm<InvoiceItemFormValues>();

  const { mutateAsync: createMutation, isLoading: isLoadingCreate } =
    useCreateInvoiceNoteItems();

  const history = useHistory();

  const invoiceType = useLocation().pathname.split('/datas')[0].slice(20, 23);

  function handleTabError(key: keyof typeof initialTabErrors) {
    setTabErrors((state) => ({ ...state, [key]: true }));
  }

  const handleSubmit = async () => {
    const { itemTaxes, ...formValues } = await form.validateFields();

    const obj = {
      ...formValues,
      invoiceId: Number(invoiceId),
      partId: undefined,
      taskId: undefined,
      type: TypeEnum.task,
      consumableId: undefined,
      itemTaxes: {
        ...itemTaxes,
        withheld: itemTaxes.withheld
          ? Boolean(JSON.parse(itemTaxes.withheld))
          : undefined,
        federalTaxesWithheld: itemTaxes.federalTaxesWithheld
          ? Boolean(JSON.parse(itemTaxes.federalTaxesWithheld))
          : undefined,
      },
    };

    await createMutation(obj);

    history.push(`/app/fiscal/invoice/${invoiceType}/${invoiceId}/noteItens`);
  };

  async function onSubmit() {
    setLoadingValidate(true);
    try {
      setTabErrors(initialTabErrors);

      const general = await form
        .validateFields(['description', 'unitMeasurement', 'valueUnitary'])
        .catch(() => handleTabError('general'));

      const values = await form
        .validateFields(['grossServiceValue'])
        .catch(() => handleTabError('values'));

      const iss = await form
        .validateFields([['itemTaxes', 'rate']])
        .catch(() => handleTabError('iss'));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hasDataValues = (data: any) =>
        Object.values(data).every((value) => !!value);

      if (
        !general ||
        !hasDataValues(general) ||
        !values ||
        !hasDataValues(values) ||
        !iss ||
        !hasDataValues(iss)
      ) {
        throw new Error('Preencha todos os campos');
      }

      handleSubmit();
    } catch {
      message.error('Há campos a serem validados!');
    } finally {
      setLoadingValidate(false);
    }
  }

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <SaveButton
          type="primary"
          onClick={onSubmit}
          loading={isLoadingCreate || loadingValidate}
          disabled={loadingValidate}
        >
          {intl({ id: 'commons.entity.save' })}
        </SaveButton>
      }
    >
      <Helmet title={intl({ id: route.name })} />

      <InvoiceNoteItemsSelectServiceTabs form={form} tabErrors={tabErrors} />
    </TabContentWrapper>
  );
}
